// src/encounter/care-plan-tab/care-plan-board.ts
// The plan board's derived facts, as Samer's demo (?view=protocol-doc) computes them, from the record only:
//  - the current week (plan start + today; none when the plan has no start),
//  - the care team strip rows (A2 team members, their lanes and "x/y signed" from the activities they own),
//  - "Needs you now" (the viewer's unsigned items; the other lanes' unsigned items running this week),
//  - each lane's tally (the folded-lane summary), the bars' tracks (items that overlap stack), the retests by week.
// Nothing here invents a value: a fact with no source is null and the view says so (or leaves the place out).
import type { TeamMemberWire } from '@/types/l360-api';
import { CARE_PLAN_LANE_IDS, type CarePlanActivityView, type CarePlanLane } from '../lib/care-plan-view';
import { PLAN_WEEKS } from './care-plan-labels';
import type { ActivityAction } from './use-activity-action';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Lanes the viewer folded (true) or opened (false); a lane not listed takes its default. */
export type LaneFold = Partial<Record<CarePlanLane, boolean>>;

/** The id of an item's bar, so "Open" in Needs you now can bring it into view. */
export const barId = (carePlanId: string, activityId: string) => `care-plan-item-${carePlanId}-${activityId}`;

/** A date-only value ("2026-11-02") is the clinic's local day, not UTC midnight (new Date('YYYY-MM-DD') parses as UTC). */
function parsePlanDate(value: string): Date {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  return dateOnly ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])) : new Date(value);
}

/** "15 Apr 2026" (the demo's date form); an unreadable date is shown as sent. */
export function formatPlanDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = parsePlanDate(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Review (accept) for a draft, Sign for an accepted item, nothing once signed. */
export function activityActionFor(a: CarePlanActivityView): ActivityAction | null {
  if (a.status === 'signed') return null;
  return a.status === 'accepted' ? 'sign' : 'accept';
}

/** The plan week today falls in (1..12), from the plan's start. null = no start, or today is outside the 12 weeks. */
export function currentPlanWeek(start: string | null | undefined, now: Date = new Date()): number | null {
  if (!start) return null;
  const s = parsePlanDate(start);
  if (Number.isNaN(s.getTime())) return null;
  // Whole calendar days between the two local dates: Math.round, so a 23 h or 25 h day (a DST change) still counts as one.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDay = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const days = Math.round((today.getTime() - startDay.getTime()) / DAY_MS);
  if (days < 0) return null;
  const week = Math.floor(days / 7) + 1;
  return week <= PLAN_WEEKS ? week : null;
}

const LANE_ORDER: Record<CarePlanLane, number> = Object.fromEntries(CARE_PLAN_LANE_IDS.map((l, i) => [l, i])) as Record<CarePlanLane, number>;

/** The demo's order (Moe): accepted first, then the earliest week, then the lane order. */
export function byDemoOrder(a: CarePlanActivityView, b: CarePlanActivityView): number {
  const acc = (x: CarePlanActivityView) => (x.status === 'accepted' ? 0 : 1);
  return acc(a) - acc(b) || a.weekFrom - b.weekFrom || LANE_ORDER[a.lane] - LANE_ORDER[b.lane];
}

/** Who is reading: the signed-in user's id, name and whether the app reads them as a physician (auth/roles isDoctor). */
export interface CarePlanViewer {
  userId: string | null;
  name: string | null;
  isPhysician: boolean;
}

/**
 * The items waiting for the viewer: unsigned, and owned by the viewer's user id. A physician also gets the unsigned
 * Physician orders with no named owner (the server alone decides who may sign: 403 otherwise).
 */
export function isMine(a: CarePlanActivityView, viewer: CarePlanViewer | null | undefined): boolean {
  if (!viewer?.userId) return false;
  if (a.ownerUserId) return a.ownerUserId === viewer.userId;
  return viewer.isPhysician && a.lane === 'physician-orders';
}

export function needsYouNow(
  activities: readonly CarePlanActivityView[],
  viewer: CarePlanViewer | null | undefined,
  week: number | null,
): { mine: CarePlanActivityView[]; thisWeek: CarePlanActivityView[]; myLanes: CarePlanLane[] } {
  const open = activities.filter((a) => a.status !== 'signed');
  const mine = open.filter((a) => isMine(a, viewer)).sort(byDemoOrder);
  const ids = new Set(mine.map((a) => a.id));
  const myLanes = CARE_PLAN_LANE_IDS.filter((l) => activities.some((a) => a.lane === l && isMine(a, viewer)));
  // "Week N, other lanes": the open items running this week in lanes the viewer does not own.
  const thisWeek =
    week === null
      ? []
      : open
          .filter((a) => !ids.has(a.id) && !myLanes.includes(a.lane) && a.weekFrom <= week && week <= a.weekTo)
          .sort(byDemoOrder);
  return { mine, thisWeek, myLanes };
}

/** The demo's words for the viewer's group ("In your orders"), from the lanes the viewer owns items in. */
export function myLanesTitle(lanes: readonly CarePlanLane[]): string {
  const key = [...lanes].sort().join('+');
  const words: Record<string, string> = {
    'physician-orders': 'your orders',
    'nutrition+supplements': 'your nutrition and supplement lanes',
    nutrition: 'your nutrition lane',
    supplements: 'your supplement lane',
    exercise: 'your exercise lane',
    coaching: 'your coaching lane',
    physiotherapy: 'your physiotherapy lane',
  };
  return `In ${words[key] ?? 'your lanes'}`;
}

/** The demo's "signs …" words per lane; nutrition + supplements read "nutrition & supplements". */
const SIGNS_WORD: Record<CarePlanLane, string> = {
  'physician-orders': 'orders & medications',
  nutrition: 'nutrition',
  exercise: 'exercise',
  coaching: 'coaching',
  supplements: 'supplements',
  physiotherapy: 'physiotherapy',
};

export interface TeamStripMember {
  key: string;
  /** Only the signed-in user's own name is known here: A2 carries no names. null = say the role only. */
  name: string | null;
  role: string;
  /** The lanes this member owns items in, in the board's order. [] = no lane on this plan. */
  lanes: CarePlanLane[];
  signed: number;
  total: number;
}

/** "signs orders & medications" / "no lane on this plan" (the demo's words). */
export function signsText(lanes: readonly CarePlanLane[]): string {
  return lanes.length ? `signs ${lanes.map((l) => SIGNS_WORD[l]).join(' & ')}` : 'no lane on this plan';
}

function owns(m: TeamMemberWire, a: CarePlanActivityView): boolean {
  if (m.ownerUserId && a.ownerUserId) return m.ownerUserId === a.ownerUserId;
  return !!m.practitionerId && m.practitionerId === a.ownerPractitionerId;
}

/**
 * One card per person. A2 lists a contributor and their task separately, and a row can carry the user id, the
 * practitioner id or both: rows join when either id matches (a row with neither joins others of its role).
 */
export function teamStrip(
  members: readonly TeamMemberWire[],
  activities: readonly CarePlanActivityView[],
  viewer: CarePlanViewer | null | undefined,
): TeamStripMember[] {
  const idsOf = (m: TeamMemberWire) =>
    m.ownerUserId || m.practitionerId
      ? [m.ownerUserId && `u:${m.ownerUserId}`, m.practitionerId && `p:${m.practitionerId}`].filter((x): x is string => !!x)
      : [`role:${m.role}`];
  let groups: { ids: Set<string>; rows: TeamMemberWire[] }[] = [];
  for (const m of members) {
    const ids = idsOf(m);
    const hits = groups.filter((g) => ids.some((id) => g.ids.has(id)));
    const merged = { ids: new Set([...ids, ...hits.flatMap((g) => [...g.ids])]), rows: [...hits.flatMap((g) => g.rows), m] };
    groups = [...groups.filter((g) => !hits.includes(g)), merged];
  }
  // Keep the first appearance order of A2's list.
  groups.sort((a, b) => members.indexOf(a.rows[0]) - members.indexOf(b.rows[0]));
  return groups.map(({ ids, rows }) => {
    const key = [...ids].sort()[0];
    const isViewer = !!viewer?.userId && rows.some((m) => m.ownerUserId === viewer.userId);
    // The viewer's card counts what "Needs you now" counts as theirs (isMine: a physician also has the unowned physician orders).
    const owned = activities.filter((a) => rows.some((m) => owns(m, a)) || (isViewer && isMine(a, viewer)));
    return {
      key,
      name: isViewer ? viewer?.name ?? null : null,
      role: rows[0].role,
      lanes: CARE_PLAN_LANE_IDS.filter((l) => owned.some((a) => a.lane === l)),
      signed: owned.filter((a) => a.status === 'signed').length,
      total: owned.length,
    };
  });
}

/** The demo's folded-lane summary (Foe): "2 items · 1 accepted, unsigned · 1 AI draft". */
export function laneTally(items: readonly CarePlanActivityView[]): string {
  const n = (s: CarePlanActivityView['status']) => items.filter((a) => a.status === s).length;
  const parts = [`${items.length} item${items.length === 1 ? '' : 's'}`];
  if (n('signed')) parts.push(`${n('signed')} signed`);
  if (n('accepted')) parts.push(`${n('accepted')} accepted, unsigned`);
  if (n('ai-draft')) parts.push(`${n('ai-draft')} AI draft${n('ai-draft') === 1 ? '' : 's'}`);
  if (n('draft')) parts.push(`${n('draft')} draft${n('draft') === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/** The board's weeks for a bar, clamped to W1..W12. */
function span(a: CarePlanActivityView): [number, number] {
  const from = Math.min(Math.max(1, a.weekFrom), PLAN_WEEKS);
  return [from, Math.min(Math.max(from, a.weekTo), PLAN_WEEKS)];
}

/** A bar needs about four weeks for its words (the demo widens a short bar to fit them), so packing counts that width. */
export const MIN_BAR_WEEKS = 4;

/**
 * The weeks a bar is drawn over: its own weeks, widened to MIN_BAR_WEEKS for its words (the demo widens a short bar).
 * Near the end it widens to the left, so it stays inside W12. Packing and the grid both use this span.
 */
export function drawnWeeks(a: CarePlanActivityView): { from: number; to: number } {
  const [from, to] = span(a);
  const start = Math.min(from, Math.max(1, PLAN_WEEKS - MIN_BAR_WEEKS + 1));
  return { from: start, to: Math.max(to, Math.min(PLAN_WEEKS, start + MIN_BAR_WEEKS - 1)) };
}

/** The demo's packing (Toe): bars that do not overlap share a track; each bar gets its 0-based track. */
export function packBars(bars: readonly CarePlanActivityView[]): { track: Map<string, number>; tracks: number } {
  const ends: number[] = [];
  const track = new Map<string, number>();
  for (const a of [...bars].sort((x, y) => drawnWeeks(x).from - drawnWeeks(y).from)) {
    const { from, to: end } = drawnWeeks(a);
    let t = ends.findIndex((e) => e < from);
    if (t === -1) {
      t = ends.length;
      ends.push(end);
    } else ends[t] = end;
    track.set(a.id, t);
  }
  return { track, tracks: Math.max(1, ends.length) };
}

/** The demo's retest marks (woe): the lane's retests grouped by their week; a group's state is its weakest item's. */
export interface RetestMark {
  week: number;
  items: CarePlanActivityView[];
  state: 'signed' | 'accepted' | 'draft' | 'ai-draft';
  /** One item: its title without a trailing " retest" (the demo's label). Several: none (the count says it). */
  label: string;
  /** The retests are due after the board's 12 weeks and are drawn on W12: the mark says "after W12". */
  past: boolean;
  /** "w<week>", or "past" for the retests after W12 (their own mark, apart from the W12 ones). */
  key: string;
}

export function retestMarks(retests: readonly CarePlanActivityView[]): RetestMark[] {
  const groups = new Map<string, CarePlanActivityView[]>();
  for (const r of retests) {
    const key = r.weekFrom > PLAN_WEEKS ? 'past' : `w${span(r)[0]}`;
    groups.set(key, [...(groups.get(key) ?? []), r]);
  }
  return [...groups.entries()]
    .map(([key, items]) => ({
      key,
      week: key === 'past' ? PLAN_WEEKS : span(items[0])[0],
      items,
      state: (items.some((i) => i.status === 'ai-draft')
        ? 'ai-draft'
        : items.some((i) => i.status === 'draft')
          ? 'draft'
          : items.every((i) => i.status === 'signed')
            ? 'signed'
            : 'accepted') as RetestMark['state'],
      label: items.length === 1 ? items[0].title.replace(/ retest$/i, '') : '',
      past: key === 'past',
    }))
    .sort((x, y) => x.week - y.week || Number(x.past) - Number(y.past));
}
